import { useEffect, useMemo, useState } from 'react';
import { Fade, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Service } from '../../../../entities';
import useStyles from './styles';
import CommonAreaPanel from './components/CommonAreaPanel';
import { AddButton, EditableBloc, IconTitle, VerticalLine } from '../../../../components';
import NoContent from './components/NoContent';
import FlexContainer from '../../../../components/FlexContainer';
import toCurrency from '../../../../utils/currency';
import getDefaultImage from './getDefaultImage';
import { useFetch, useServices, useStorage } from '../../../../hooks';

export const CommonAreas = (): JSX.Element => {
  const styles = useStyles();
  const { id: condominiumId } = useParams<{ id: string }>();
  const [services, setServices] = useState<Service[]>([]);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [selectedService, setSelectedService] = useState<Service>({} as Service);
  const { spacing } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isDelete, setIsDelete] = useState(false);
  const repository = useServices(condominiumId!);
  const { data, loading, refetch } = useFetch(repository.fetchAll);
  const storage = useStorage();

  useEffect(() => {
    setServices(data ?? []);
    setIsPanelVisible(false);
  }, [data]);

  const handleClickEdit = (value?: Service) => {
    if (value) {
      setSelectedService(value);
    }
    setIsPanelVisible(true);
  };

  const handleSetPanelOpen = () => {
    setSelectedService({} as Service);
    setIsPanelVisible(!isPanelVisible);
  };

  const showNoContent = useMemo(() => !isPanelVisible && services.length === 0, [isPanelVisible, services]);

  const handleSetSelectedService = (value: Partial<Service>) => {
    setSelectedService({ ...selectedService, ...value });
  };

  const handleUpload = (file: File | null, isDeletedUpload = false) => {
    setFileToUpload(file);
    setIsDelete(isDeletedUpload);
  };

  const handleFetchImage = async (service: Service) => {
    if (fileToUpload === null && isDelete === false) return;

    let imageUrl: string | null = null;

    if (fileToUpload) {
      const extension = fileToUpload.name.split('.').pop();

      imageUrl = await storage.upload(
        `/condominiums/${condominiumId}/common-areas/${service.id}.${extension}`,
        fileToUpload
      );
    }

    if (imageUrl !== service.imageUrl) {
      await repository.update({ imageUrl, id: service.id });
    }
  };

  const handleCreate = async (service: Service) => {
    const result = await repository.create(service);
    await handleFetchImage(result);
  };

  const handleUpdate = async (service: Service) => {
    await repository.update({ ...service, price: service.price || null });
    await handleFetchImage(service);
  };

  const handleSubmit = async (service: Service) => {
    try {
      if (service.id) {
        await handleUpdate(service);
      } else {
        await handleCreate(service);
      }

      enqueueSnackbar('Área comum salva com sucesso!', { variant: 'success' });
      refetch();
    } catch (err) {
      enqueueSnackbar('Não foi possível salvar a área comum', { variant: 'error' });
    }
  };

  if (loading && services.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de serviços" />
          <FlexContainer>
            {services.map((service) => (
              <EditableBloc<Service>
                label={service.label}
                width={`calc(33.3% - ${spacing(1.33)})`}
                maxWidth="600px"
                minWidth="300px"
                prepend={
                  <div
                    className={styles.image}
                    style={{ backgroundImage: `url(${service.imageUrl || getDefaultImage(service.type)})` }}
                  />
                }
                value={service}
                key={service.id}
                onClickEdit={handleClickEdit}
              >
                <div className={styles.info}>
                  <div>Reservas simultâneas: {service.maxSimultaneosReservations}</div>
                  <div>Preço: {service.price ? toCurrency(service.price) : 'GRATUITO'}</div>
                </div>
              </EditableBloc>
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <CommonAreaPanel
        visible={isPanelVisible}
        service={selectedService}
        file={fileToUpload}
        handleUpload={handleUpload}
        handleSetSelectedService={handleSetSelectedService}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
      />
      {!isPanelVisible && <AddButton label="adicionar área" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default CommonAreas;
